import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type {
  TypeGetReturnOrdersReturnReceiptIdParams,
  TypeGetReturnOrdersReturnReceiptIdResponse,
  TypeReturnDetailData,
} from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * [반품 신청/조회] 반품 신청 상세 조회 api 요청 훅
 */
const useGetReturnOrdersReturnReceiptId = () => {
  const { runAsync } = useAsyncHandler();

  const getReturnOrdersReturnReceiptId = useAPI<
    TypeGetReturnOrdersReturnReceiptIdParams,
    TypeGetReturnOrdersReturnReceiptIdResponse
  >({
    endpoint: API_ENDPOINT.CLAIM.GET_RETURN_ORDERS_RETURN_RECEIPT_ID,
    method: 'get',
  });

  /**
   * 상품 코드가 같은 단위끼리 묶어서 포맷팅하는 함수
   * - 상품코드 기준으로 그룹핑
   * - 검수 단위 별 정보는 inspectionDetails 배열에 추가
   */
  const formatReturnDetailData = (data: TypeGetReturnOrdersReturnReceiptIdResponse['data']): TypeReturnDetailData => {
    const { returnGoodsInspections } = data;

    const groupedByGoodsCode: Record<string, TypeReturnDetailData['returnGoodsInspections'][number]> = {};

    returnGoodsInspections.forEach((inspection) => {
      const {
        goodsCode,
        goodsName,
        returnQuantity,
        returnReason,
        inspectionQuantity,
        inboundStatus,
        inspectionStatus,
        inspectionImageUrls,
        inspectionAt,
        unlistedYn,
      } = inspection;

      if (!groupedByGoodsCode[goodsCode]) {
        groupedByGoodsCode[goodsCode] = {
          goodsCode,
          goodsName,
          returnQuantity,
          returnReason,
          inspectionDetails: [],
        };
      }

      // 검수 단위 별 정보 추가
      groupedByGoodsCode[goodsCode].inspectionDetails.push({
        inspectionQuantity,
        inboundStatus,
        inspectionStatus,
        inspectionImageUrls,
        inspectionAt,
        unlistedYn,
      });
    });

    const formattedReturnGoodsInspections = Object.values(groupedByGoodsCode);

    return {
      ...data,
      returnGoodsInspections: formattedReturnGoodsInspections,
    };
  };

  const requestReturnOrdersDetail = async (
    { returnReceiptId }: TypeGetReturnOrdersReturnReceiptIdParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeReturnDetailData) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getReturnOrdersReturnReceiptId({ pathParams: `/${returnReceiptId}` }), {
      onSuccess: ({ data }) => {
        const formattedData = formatReturnDetailData(data);
        onSuccess?.(formattedData);
      },
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestReturnOrdersDetail };
};

export default useGetReturnOrdersReturnReceiptId;

const INSPECTION_IMAGE_URL = 'https://kurly-e2e.s3.ap-northeast-2.amazonaws.com/claim/inspection-01.jpg';

/** 검수 처리 상세는 상품코드로 묶여 한 행에 검수 단위가 여러 줄로 쌓인다. 첫 상품은 검수 2건, 둘째 상품은 1건. */
const returnDetailResponse: TypeGetReturnOrdersReturnReceiptIdResponse = {
  data: {
    returnReceiptId: 'RT202605190003',
    clientOrderCode: 'CL-20260518-0003',
    outboundOrderCode: 'OB202605180003',
    sellerCode: 'SL0001',
    returnCourier: [{ code: 'LOTTE', name: '롯데' }],
    status: [{ code: 'RETURN_INSPECTION_ING', name: '검수중' }],
    totalBox: 2,
    member: { name: '박배송', phoneNumber: '01012345678' },
    returnPlace: {
      primaryAddress: '서울특별시 강남구 테헤란로 133',
      secondaryAddress: '컬리타워 10층',
      accessMethod: '공동현관 비밀번호',
      accessDetail: '#1234',
      pickupType: '문 앞',
    },
    returnOrderTrackings: [
      {
        invoiceNumber: '250012345601',
        status: { code: 'COURIER_DELIVERED_COMPLETE', name: '회수완료' },
        statusAt: '2026-05-19 07:40:00',
        invoiceTrackingUrl: 'https://tracking.example.com/250012345601',
      },
      {
        invoiceNumber: '250012345602',
        status: { code: 'COURIER_DELIVERED_FAIL', name: '회수실패' },
        statusAt: '2026-05-19 07:55:00',
        failureReason: '고객 부재로 회수하지 못했습니다.',
        invoiceTrackingUrl: 'https://tracking.example.com/250012345602',
      },
    ],
    returnGoodsInspections: [
      {
        goodsCode: 'G00000001',
        goodsName: '[컬리] 유기농 바나나 1kg',
        returnQuantity: 3,
        returnReason: { code: 'CHANGE_OF_MIND', name: '단순변심' },
        inboundStatus: '입하완료',
        inspectionQuantity: 2,
        inspectionStatus: '검수성공',
        unlistedYn: false,
        inspectionAt: '2026-05-19 08:10:00',
        inspectionImageUrls: [INSPECTION_IMAGE_URL],
      },
      {
        goodsCode: 'G00000001',
        goodsName: '[컬리] 유기농 바나나 1kg',
        returnQuantity: 3,
        returnReason: { code: 'CHANGE_OF_MIND', name: '단순변심' },
        inboundStatus: '입하완료',
        inspectionQuantity: 1,
        inspectionStatus: '검수실패',
        unlistedYn: false,
        inspectionAt: '2026-05-19 08:12:00',
        inspectionImageUrls: [INSPECTION_IMAGE_URL],
      },
      {
        goodsCode: 'G00000002',
        goodsName: '[컬리] 무항생제 특란 20구',
        returnQuantity: 1,
        returnReason: { code: 'DEFECT', name: '상품불량' },
        inboundStatus: '입하전',
        inspectionQuantity: 0,
        inspectionStatus: '검수전',
        unlistedYn: false,
        inspectionAt: '',
        inspectionImageUrls: [],
      },
    ],
    returnReceiptAt: '2026-05-19 08:13:00',
  },
};

registerMockAPI<TypeGetReturnOrdersReturnReceiptIdResponse>({
  method: 'get',
  // 숫자가 하나 이상 들어간 요청번호만 받아 `/return-orders/existence` 를 가로채지 않는다.
  endpoint: '/api-oms/return-orders/:returnReceiptId([A-Z]{0,}\\d[A-Z0-9]{0,})',
  label: '반품교환/반품신청조회/상세',
  scenarios: [
    { name: '정상 응답 — 검수 2개 상품(3건)·송장 2건', response: returnDetailResponse },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '반품 상세를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
